import { TaskModel } from '@/Domains/Task/Logic/TaskModel';
import { makeEntitySlice } from './create-entity-slice';

import {
    UserModel,
    CountryModel,
    UserSessionModel,
    ContactModel,
    ChallengeModel,
    NeedModel,
    MandateModel,
    PropertyModel,
    PublicationModel, 
    MatchingModel, 
    TransactionModel,
    MediaFileModel,
    ActivityModel,
    PlanningModel,
    BirthWishModel,
    DocumentModel,
    PermissionModel,
    LeaseModel,
    AgentTargetModel
} from '@/Domains';

/**
 * =====================================================
 * Entity Slices Registry
 * =====================================================
 *
 * Centralized registration of all domain entity slices.
 * Each slice is instantiated using the generic `makeEntitySlice` factory.
 *
 * Responsibilities:
 * - Bind a Redux slice to a specific domain model
 * - Expose a consistent set of actions and reducers
 * - Ensure predictable state structure across the application
 * - Reduce boilerplate for adding new domain entities
 *
 * Author: Samano CASTRE
 * Date: January 2026
 */


/**
 * -----------------------------------------------------
 * Slice pattern
 * -----------------------------------------------------
 *
 * const slice = makeEntitySlice<Model>('sliceName');
 * export const { actions: sliceActions, reducer: sliceReducer } = slice;
 *
 * This pattern applies to all domain entities, ensuring:
 * - standardized API for CRUD, status, and mode handling
 * - consistent naming conventions
 * - plug-and-play integration with the Redux store
 */


/**
 * -----------------------------
 * User entity slice
 * -----------------------------
 */
export const userSlice = makeEntitySlice<UserModel>('users');
export const { actions: userActions, reducer: userReducer } = userSlice;


/**
 * -----------------------------
 * Contact entity slice
 * -----------------------------
 */
export const contactSlice = makeEntitySlice<ContactModel>('contacts');
export const { actions: contactActions, reducer: contactReducer } = contactSlice;


/**
 * -----------------------------
 * Challenge entity slice
 * -----------------------------
 */
export const challengeSlice = makeEntitySlice<ChallengeModel>('challenges');
export const { actions: challengeActions, reducer: challengeReducer } = challengeSlice;


/**
 * -----------------------------
 * User entity slice
 * -----------------------------
 */
export const userSessionSlice = makeEntitySlice<UserSessionModel>('userSessions');
export const { actions: userSessionActions, reducer: userSessionReducer } = userSessionSlice;


/**
 * -----------------------------
 * Need entity slice
 * -----------------------------
 */
export const needSlice = makeEntitySlice<NeedModel>('needs');
export const { actions: needActions, reducer: needReducer } = needSlice;

/**
 * -----------------------------
 * Country entity slice
 * -----------------------------
 */
export const countrySlice = makeEntitySlice<CountryModel>('countries');
export const { actions: countryActions, reducer: countryReducer } = countrySlice;


/**
 * -----------------------------
 * Publication entity slice
 * -----------------------------
 */
export const publicationSlice = makeEntitySlice<PublicationModel>('publications');
export const { actions: publicationActions, reducer: publicationReducer } = publicationSlice;

/**
 * -----------------------------
 * AgentTarget entity slice
 * -----------------------------
 */
export const agentTargetSlice = makeEntitySlice<AgentTargetModel>('agentTargets');
export const { actions: agentTargetActions, reducer: agentTargetReducer } = agentTargetSlice;


/**
 * -----------------------------
 * Property entity slice
 * -----------------------------
 */
export const propertySlice = makeEntitySlice<PropertyModel>('properties');
export const { actions: propertyActions, reducer: propertyReducer } = propertySlice;


/**
 * -----------------------------
 * Mandate entity slice
 * -----------------------------
 */
export const mandateSlice = makeEntitySlice<MandateModel>('mandates');
export const { actions: mandateActions, reducer: mandateReducer } = mandateSlice;


/**
 * -----------------------------
 * Transaction entity slice
 * -----------------------------
 */
export const transactionSlice = makeEntitySlice<TransactionModel>('transactions');
export const { actions: transactionActions, reducer: transactionReducer } = transactionSlice;


/**
 * -----------------------------
 * Lease entity slice
 * -----------------------------
 */
export const leaseSlice = makeEntitySlice<LeaseModel>('leases');
export const { actions: leaseActions, reducer: leaseReducer } = leaseSlice;


/**
 * -----------------------------
 * Matching entity slice
 * -----------------------------
 */
export const matchingSlice = makeEntitySlice<MatchingModel>('matchings');
export const { actions: matchingActions, reducer: matchingReducer } = matchingSlice;


/**
 * -----------------------------
 * MediaFile entity slice
 * -----------------------------
 */
export const mediaFileSlice = makeEntitySlice<MediaFileModel>('mediaFiles');
export const { actions: mediaFileActions, reducer: mediaFileReducer } = mediaFileSlice;


/**
 * -----------------------------
 * Task entity slice
 * -----------------------------
 */
export const taskSlice = makeEntitySlice<TaskModel>('tasks');
export const { actions: taskActions, reducer: taskReducer } = taskSlice;


/**
 * -----------------------------
 * BirthWish entity slice
 * -----------------------------
 */
export const birthWishSlice = makeEntitySlice<BirthWishModel>('birthWishes');
export const { actions: birthWishActions, reducer: birthWishReducer } = birthWishSlice;


/**
 * -----------------------------
 * Document entity slice
 * -----------------------------
 */
export const documentSlice = makeEntitySlice<DocumentModel>('documents');
export const { actions: documentActions, reducer: documentReducer } = documentSlice;


/**
 * -----------------------------
 * Permission entity slice
 * -----------------------------
 */
export const permissionSlice = makeEntitySlice<PermissionModel>('permissions');
export const { actions: permissionActions, reducer: permissionReducer } = permissionSlice;


/**
 * -----------------------------
 * Activity entity slice
 * -----------------------------
 */
export const activitySlice = makeEntitySlice<ActivityModel>('activities');
export const { actions: activityActions, reducer: activityReducer } = activitySlice;


/**
 * -----------------------------
 * Planning entity slice
 * -----------------------------
 */
export const planningSlice = makeEntitySlice<PlanningModel>('plannings');
export const { actions: planningActions, reducer: planningReducer } = planningSlice;
